import { useCallback, useEffect, useRef, useState } from "react";
import { Reorder } from "framer-motion";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import {
  Archive,
  Check,
  Copy,
  Eye,
  FilePlus2,
  GripVertical,
  ImagePlus,
  Loader2,
  Plus,
  Save,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import api from "@/lib/api";
import BlockEditor from "@/components/BlockEditor";
import BlockRenderer from "@/components/BlockRenderer";
import RichTextEditor from "@/components/RichTextEditor";
import MediaPicker from "@/components/MediaPicker";
import {AuthorSelect,CategorySelect,TagInput} from "@/components/TaxonomyControls";

const input =
  "w-full rounded-lg border border-dir-gold/20 bg-dir-elevated px-3 py-2.5 text-sm text-dir-text placeholder:text-dir-faint focus:border-dir-gold/60 focus:outline-none";
const card = "rounded-2xl border border-dir-gold/15 bg-dir-card p-5";
const singularLabel = (label) =>
  label === "News" ? "News" : label.replace(/s$/, "");
const emptyArticle = {
  title: "",
  slug: "",
  subtitle: "",
  excerpt: "",
  category: "",
  primary_category_id: null,
  tags: [],
  featured_media: null,
  featured_image: "",
  featured_image_alt: "",
  author: "",
  cms_author_id: null,
  read_time: "",
  read_time_override: null,
  publish_at: "",
  status: "draft",
  visibility: "public",
  featured: false,
  seo_title: "",
  seo_description: "",
  canonical_url: "",
  og_image: "",
  rich_content: null,
  blocks: [],
  allow_comments: true,
  allow_likes: true,
  show_view_count: true,
};

function useCmsList(resource) {
  const [items, setItems] = useState(null);
  const load = useCallback(
    async (params = {}) => {
      setItems(null);
      try {
        setItems((await api.get(`/cms/${resource}`, { params })).data);
      } catch {
        setItems([]);
      }
    },
    [resource],
  );
  useEffect(() => {
    load();
  }, [load]);
  return [items, load];
}

export function CmsDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/cms/dashboard").then((r) => setData(r.data));
  }, []);
  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-dir-gold" />;
  const labels = {
    published_articles: "Published Articles",
    draft_articles: "Draft Articles",
    pages: "Pages",
    media: "Images & Media",
    videos: "Videos",
    editors: "Editors",
  };
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {Object.entries(labels).map(([k, label]) => (
          <div className={card} key={k}>
            <div className="text-3xl font-bold text-dir-gold">
              {data.counts[k] || 0}
            </div>
            <div className="mt-1 text-sm text-dir-muted">{label}</div>
          </div>
        ))}
      </div>
      <div className={card}>
        <h3 className="font-semibold text-dir-text">Recent CMS activity</h3>
        <div className="mt-3 divide-y divide-dir-gold/10">
          {data.recent_activity.length ? (
            data.recent_activity.map((a) => (
              <div
                key={a.id}
                className="flex justify-between gap-4 py-3 text-sm"
              >
                <span className="text-dir-text">
                  {a.user_email || "System"}{" "}
                  <span className="text-dir-muted">
                    {a.action} {a.resource}
                  </span>
                </span>
                <time className="text-dir-faint">
                  {new Date(a.timestamp).toLocaleString()}
                </time>
              </div>
            ))
          ) : (
            <p className="py-6 text-sm text-dir-faint">No CMS activity yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function CmsContentManager({
  resource = "articles",
  label = "Articles",
}) {
  const [items, load] = useCmsList(resource);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const archive = async (id) => {
    if (!window.confirm(`Archive this ${singularLabel(label).toLowerCase()}?`))
      return;
    await api.delete(`/cms/${resource}/${id}`);
    toast.success("Archived");
    load();
  };
  if (editing)
    return (
      <ContentEditor
        resource={resource}
        label={label}
        initial={editing === "new" ? emptyArticle : editing}
        onClose={() => {
          setEditing(null);
          load();
        }}
      />
    );
  return (
    <div>
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-dir-faint" />
          <input
            className={`${input} pl-10`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${label.toLowerCase()}…`}
          />
        </div>
        <select
          className={`${input} lg:w-44`}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          {["draft", "review", "scheduled", "published", "archived"].map(
            (s) => (
              <option key={s}>{s}</option>
            ),
          )}
        </select>
        <button
          onClick={() => load({ q, status })}
          className="rounded-lg border border-dir-gold/30 px-4 py-2 text-sm text-dir-gold"
        >
          Filter
        </button>
        <button
          onClick={() => setEditing("new")}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-dir-gold px-4 py-2.5 text-sm font-bold text-dir-deep"
        >
          <FilePlus2 className="h-4 w-4" />
          New {singularLabel(label)}
        </button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-dir-gold/15">
        <table className="w-full text-sm">
          <thead className="bg-dir-deep text-left text-dir-faint">
            <tr>
              <th className="p-4">Title</th>
              <th className="p-4">Status</th>
              <th className="p-4">Updated</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items?.map((it) => (
              <tr key={it.id} className="border-t border-dir-gold/10">
                <td className="p-4">
                  <div className="font-medium text-dir-text">
                    {it.title || it.name}
                  </div>
                  <div className="text-xs text-dir-faint">/{it.slug}</div>
                </td>
                <td className="p-4 capitalize text-dir-muted">{it.status}</td>
                <td className="p-4 text-dir-faint">
                  {it.updated_at
                    ? new Date(it.updated_at).toLocaleString()
                    : "—"}
                </td>
                <td className="p-4">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setEditing(it)}
                      className="rounded-md border border-dir-gold/20 px-3 py-1.5 text-dir-gold"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => archive(it.id)}
                      title="Archive"
                      className="p-2 text-red-400"
                    >
                      <Archive className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items && !items.length && (
          <p className="p-10 text-center text-dir-faint">
            No {label.toLowerCase()} found.
          </p>
        )}
        {!items && (
          <Loader2 className="m-8 h-6 w-6 animate-spin text-dir-gold" />
        )}
      </div>
    </div>
  );
}

function ContentEditor({ resource, label, initial, onClose }) {
  const [form, setForm] = useState({ ...emptyArticle, ...initial });
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState("Saved");
  const [preview, setPreview] = useState(false);
  const timer = useRef();
  const contentType = ({articles:"article",blogs:"blog",news:"news",events:"event",opportunities:"opportunity",media:"media"})[resource] || resource.replace(/s$/, "");
  const calculatedReadTime = Math.max(1, Math.ceil((JSON.stringify([form.rich_content, form.blocks, form.excerpt]).match(/\b[\w’'-]+\b/g) || []).length / 200));
  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaveState("Unsaved changes");
  };
  const setFeatured = (media) => {
    setForm((f) => ({
      ...f,
      featured_media: media,
      featured_media_id: media?.id || null,
      featured_image:
        media?.public_url || media?.url || media?.external_url || "",
      featured_image_alt: media?.alt_text || "",
      featured_image_caption: media?.caption || "",
    }));
    setSaveState("Unsaved changes");
  };
  const save = useCallback(
    async (override = {}) => {
      setSaving(true);
      setSaveState("Saving…");
      const payload = {
        ...form,
        ...override,
        tags: Array.isArray(form.tags)
          ? form.tags
          : String(form.tags || "")
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean),
      };
      try {
        const result = form.id
          ? await api.put(`/cms/${resource}/${form.id}`, payload)
          : await api.post(`/cms/${resource}`, payload);
        setForm(result.data);
        setSaveState("Saved");
        toast.success(override.status === "published" ? "Published" : "Saved");
      } catch (e) {
        setSaveState("Save failed");
        toast.error(e.response?.data?.detail || "Save failed");
      }
      setSaving(false);
    },
    [form, resource],
  );
  useEffect(() => {
    if (!form.id || saveState !== "Unsaved changes") return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save(), 3500);
    return () => clearTimeout(timer.current);
  }, [form, form.id, save, saveState]);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button onClick={onClose} className="text-sm text-dir-muted">
            ← Back to {label}
          </button>
          <h2 className="mt-2 text-2xl font-bold text-dir-text">
            {form.id ? "Edit" : "New"} {singularLabel(label)}
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-dir-faint">{saveState}</span>
          <button
            onClick={() => setPreview(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-dir-gold/30 px-4 py-2 text-sm text-dir-gold"
          >
            <Eye className="h-4 w-4" />
            Preview
          </button>
          <button
            disabled={saving}
            onClick={() => save({ status: "draft" })}
            className="inline-flex items-center gap-2 rounded-lg border border-dir-gold/30 px-4 py-2 text-sm text-dir-text"
          >
            <Save className="h-4 w-4" />
            Save Draft
          </button>
          <button
            disabled={saving}
            onClick={() => save({ status: "published" })}
            className="inline-flex items-center gap-2 rounded-lg bg-dir-gold px-4 py-2 text-sm font-bold text-dir-deep"
          >
            <Check className="h-4 w-4" />
            Publish
          </button>
          <button disabled={saving || !form.publish_at} onClick={() => save({ status: "scheduled" })} className="inline-flex items-center gap-2 rounded-lg border border-dir-gold/30 px-4 py-2 text-sm text-dir-gold disabled:opacity-40">Schedule</button>
        </div>
      </div>
      <div className="grid gap-5 xl:grid-cols-[1fr_320px]">
        <div className="space-y-5">
          <div className={card}>
            <MediaPicker
              label="Featured Media"
              folder={resource}
              value={
                form.featured_media ||
                (form.featured_image
                  ? {
                      public_url: form.featured_image,
                      alt_text: form.featured_image_alt,
                      caption: form.featured_image_caption,
                    }
                  : null)
              }
              onChange={setFeatured}
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm"
            />
          </div>
          <div className={card}>
            <input
              className={`${input} text-xl font-semibold`}
              placeholder="Title"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
            />
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <input
                className={input}
                placeholder="Slug (automatic if blank)"
                value={form.slug}
                onChange={(e) => set("slug", e.target.value)}
              />
              <input
                className={input}
                placeholder="Subtitle"
                value={form.subtitle}
                onChange={(e) => set("subtitle", e.target.value)}
              />
            </div>
            <textarea
              className={`${input} mt-3`}
              rows={3}
              placeholder="Excerpt"
              value={form.excerpt}
              onChange={(e) => set("excerpt", e.target.value)}
            />
          </div>
          <div className={card}>
            <h3 className="mb-3 font-semibold text-dir-text">
              Rich article body
            </h3>
            <RichTextEditor
              value={form.rich_content}
              onChange={(v) => set("rich_content", v)}
            />
          </div>
          <div className={card}>
            <h3 className="mb-3 font-semibold text-dir-text">
              Structured content blocks
            </h3>
            <BlockEditor
              value={form.blocks}
              onChange={(v) => set("blocks", v)}
              mediaFolder={resource}
            />
          </div>
        </div>
        <aside className="space-y-4">
          <div className={card}>
            <label className="text-xs uppercase text-dir-faint">Status</label>
            <select
              className={`${input} mt-2 capitalize`}
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
            >
              {[["draft", "Draft"], ["review", "Editorial Review"], ["scheduled", "Scheduled"], ["published", "Published"], ["archived", "Archived"]].map(
                ([s, statusLabel]) => (
                  <option key={s} value={s}>{statusLabel}</option>
                ),
              )}
            </select>
            <div className="mt-4"><CategorySelect contentType={contentType} value={form.primary_category_id} onChange={(id, item) => setForm((f) => ({ ...f, primary_category_id: id, category: item?.name || "" }))} /></div>
            <div className="mt-4"><AuthorSelect value={form.cms_author_id} onChange={(id, item) => setForm((f) => ({ ...f, cms_author_id: id, author: item?.name || "" }))} /></div>
            <label className="mt-4 block text-xs uppercase text-dir-faint">Schedule date and time</label>
            <input type="datetime-local" className={`${input} mt-2`} value={form.publish_at?.slice?.(0, 16) || ""} onChange={(e) => set("publish_at", e.target.value ? new Date(e.target.value).toISOString() : null)} />
            <label className="mt-4 block text-xs uppercase text-dir-faint">Read time</label>
            <div className="mt-2 rounded-lg border border-dir-gold/15 bg-dir-deep p-3 text-sm text-dir-muted">Automatically calculated: <span className="text-dir-gold">{calculatedReadTime} min</span></div>
            <input type="number" min="1" className={`${input} mt-2`} placeholder="Optional manual override" value={form.read_time_override || ""} onChange={(e) => set("read_time_override", e.target.value ? Number(e.target.value) : null)} />
            <div className="mt-4"><TagInput value={form.tags} onChange={(value) => set("tags", value)} /></div>
          </div>
          <div className={card}>
            <h3 className="font-semibold text-dir-text">Audience engagement</h3>
            <div className="mt-3 space-y-3">
              {[["allow_likes","Allow likes"],["allow_comments","Allow comments"],["show_view_count","Show view count"]].map(([key,labelText])=><label key={key} className="flex items-center justify-between text-sm text-dir-muted"><span>{labelText}</span><input type="checkbox" checked={form[key] !== false} onChange={(e)=>set(key,e.target.checked)} className="h-4 w-4 accent-dir-gold"/></label>)}
            </div>
            {form.id && <div className="mt-4 grid grid-cols-3 gap-2 border-t border-dir-gold/10 pt-4 text-center text-xs text-dir-faint"><div><b className="block text-lg text-dir-text">{form.view_count||0}</b>views</div><div><b className="block text-lg text-dir-text">{form.like_count||0}</b>likes</div><div><b className="block text-lg text-dir-text">{form.comment_count||0}</b>comments</div></div>}
          </div>
          <div className={card}>
            <h3 className="font-semibold text-dir-text">SEO</h3>
            <input
              className={`${input} mt-3`}
              placeholder="SEO title"
              value={form.seo_title}
              onChange={(e) => set("seo_title", e.target.value)}
            />
            <textarea
              className={`${input} mt-2`}
              rows={3}
              placeholder="Meta description"
              value={form.seo_description}
              onChange={(e) => set("seo_description", e.target.value)}
            />
            <input
              className={`${input} mt-2`}
              placeholder="Canonical URL"
              value={form.canonical_url}
              onChange={(e) => set("canonical_url", e.target.value)}
            />
          </div>
        </aside>
      </div>
      {preview && <PreviewModal form={form} close={() => setPreview(false)} />}
    </div>
  );
}

function PreviewModal({ form, close }) {
  return (
    <div
      className="fixed inset-0 z-[100] overflow-y-auto bg-black/85 p-4"
      onClick={close}
    >
      <div
        className="mx-auto max-w-4xl rounded-2xl bg-dir-bg p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={close} className="float-right p-2 text-dir-muted">
          <X />
        </button>
        {form.featured_image && (
          <img
            src={form.featured_image}
            alt={form.featured_image_alt}
            className="mb-8 max-h-96 w-full rounded-2xl object-cover"
          />
        )}
        <p className="text-xs uppercase tracking-widest text-dir-gold">
          {form.category}
        </p>
        <h1 className="mt-3 text-4xl font-bold text-dir-text">
          {form.title || "Untitled"}
        </h1>
        <p className="mt-4 text-lg text-dir-muted">
          {form.subtitle || form.excerpt}
        </p>
        {form.rich_content?.html && (
          <div
            className="prose prose-invert mt-8 max-w-none"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(form.rich_content.html),
            }}
          />
        )}
        <div className="mt-8">
          <BlockRenderer blocks={form.blocks} />
        </div>
      </div>
    </div>
  );
}

export function MediaLibrary() {
  const [items, load] = useCmsList("media");
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [view, setView] = useState("grid");
  const [editing, setEditing] = useState(null);
  const picker = useRef();
  const upload = async (files) => {
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      form.append(
        "folder",
        file.type.startsWith("video/") ? "videos" : "media",
      );
      form.append("alt_text", file.name.replace(/\.[^.]+$/, ""));
      setUploading(true);
      try {
        await api.post("/cms/media/upload/file", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`${file.name} uploaded to Supabase`);
      } catch (e) {
        toast.error(e.response?.data?.detail || `Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
    load();
  };
  const visible = (items || []).filter(
    (m) =>
      (!q ||
        `${m.name} ${m.filename} ${m.alt_text}`
          .toLowerCase()
          .includes(q.toLowerCase())) &&
      (!type || m.media_type === type),
  );
  const saveMeta = async () => {
    try {
      setEditing((await api.put(`/cms/media/${editing.id}`, editing)).data);
      toast.success("Media metadata saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const archive = async (m) => {
    if (
      !window.confirm(
        `Archive ${m.name}? Existing content references may stop displaying it.`,
      )
    )
      return;
    await api.delete(`/cms/media/${m.id}`);
    toast.success("Media archived");
    setEditing(null);
    load();
  };
  const asset = (m) => m.public_url || m.url;
  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-dir-text">Media Library</h2>
          <p className="text-sm text-dir-muted">
            Supabase-backed images and videos with reusable metadata.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setView(view === "grid" ? "list" : "grid")}
            className="rounded-lg border border-dir-gold/30 px-3 py-2 text-xs text-dir-gold"
          >
            {view === "grid" ? "List view" : "Grid view"}
          </button>
          <button
            onClick={() => picker.current.click()}
            className="inline-flex items-center gap-2 rounded-lg bg-dir-gold px-4 py-2 text-sm font-bold text-dir-deep"
          >
            <Upload className="h-4 w-4" />
            Upload Media
          </button>
          <input
            ref={picker}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm"
            className="hidden"
            onChange={(e) => upload([...e.target.files])}
          />
        </div>
      </div>
      <div className="mb-4 grid gap-3 sm:grid-cols-[1fr_180px]">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-dir-faint" />
          <input
            className={`${input} pl-10`}
            placeholder="Search files, alt text, captions…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          className={input}
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          <option value="">All file types</option>
          <option value="image">Images</option>
          <option value="video">Videos</option>
        </select>
      </div>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          upload([...e.dataTransfer.files]);
        }}
        className="mb-6 rounded-2xl border-2 border-dashed border-dir-gold/20 p-8 text-center text-dir-faint"
      >
        <ImagePlus className="mx-auto mb-2 h-8 w-8" />
        Drag images or videos here
        {uploading && (
          <Loader2 className="mx-auto mt-3 h-5 w-5 animate-spin text-dir-gold" />
        )}
      </div>
      <div
        className={
          view === "grid"
            ? "grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            : "space-y-2"
        }
      >
        {visible.map((m) => (
          <button
            type="button"
            onClick={() => setEditing(m)}
            key={m.id}
            className={`${card} text-left ${view === "list" ? "flex items-center gap-4" : ""}`}
          >
            {m.media_type === "image" ? (
              <img
                src={asset(m)}
                alt={m.alt_text}
                className={
                  view === "grid"
                    ? "h-40 w-full rounded-lg object-cover"
                    : "h-16 w-20 rounded-lg object-cover"
                }
              />
            ) : (
              <video
                src={asset(m)}
                className={
                  view === "grid"
                    ? "h-40 w-full rounded-lg object-cover"
                    : "h-16 w-20 rounded-lg object-cover"
                }
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="mt-3 truncate text-sm font-medium text-dir-text">
                {m.name}
              </div>
              <div className="mt-1 text-xs text-dir-faint">
                {Math.round((m.size_bytes || m.size || 0) / 1024)} KB ·{" "}
                {m.mime_type} ·{" "}
                {m.created_at
                  ? new Date(m.created_at).toLocaleDateString()
                  : ""}
              </div>
            </div>
          </button>
        ))}
      </div>
      {editing && (
        <div
          className="fixed inset-0 z-[120] overflow-y-auto bg-black/80 p-5"
          onClick={() => setEditing(null)}
        >
          <div
            className="mx-auto max-w-2xl rounded-2xl border border-dir-gold/20 bg-dir-bg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="float-right text-dir-muted"
              onClick={() => setEditing(null)}
            >
              <X />
            </button>
            {editing.media_type === "image" ? (
              <img
                src={asset(editing)}
                alt={editing.alt_text}
                className="mb-5 max-h-80 w-full rounded-xl object-contain"
              />
            ) : (
              <video
                src={asset(editing)}
                controls
                className="mb-5 max-h-80 w-full rounded-xl"
              />
            )}
            <div className="space-y-3">
              <input
                className={input}
                value={editing.name || ""}
                onChange={(e) =>
                  setEditing({ ...editing, name: e.target.value })
                }
                placeholder="Asset name"
              />
              <input
                className={input}
                value={editing.alt_text || ""}
                onChange={(e) =>
                  setEditing({ ...editing, alt_text: e.target.value })
                }
                placeholder="Alt text"
              />
              <textarea
                className={input}
                rows={3}
                value={editing.caption || ""}
                onChange={(e) =>
                  setEditing({ ...editing, caption: e.target.value })
                }
                placeholder="Caption"
              />
              <div className="text-xs text-dir-faint">
                {editing.width && editing.height
                  ? `${editing.width} × ${editing.height} · `
                  : ""}
                {Math.round((editing.size_bytes || 0) / 1024)} KB · Uploaded{" "}
                {editing.created_at
                  ? new Date(editing.created_at).toLocaleString()
                  : ""}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={saveMeta}
                  className="rounded-lg bg-dir-gold px-4 py-2 text-sm font-bold text-dir-deep"
                >
                  Save metadata
                </button>
                <button
                  onClick={() =>
                    navigator.clipboard
                      .writeText(asset(editing))
                      .then(() => toast.success("URL copied"))
                  }
                  className="inline-flex items-center gap-1 rounded-lg border border-dir-gold/30 px-4 py-2 text-sm text-dir-gold"
                >
                  <Copy className="h-4 w-4" />
                  Copy URL
                </button>
                <button
                  onClick={() => archive(editing)}
                  className="inline-flex items-center gap-1 rounded-lg border border-red-500/30 px-4 py-2 text-sm text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                  Archive
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function SingletonManager({ resource, label, fields }) {
  const [items] = useCmsList(resource);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (items)
      setForm(
        items[0] || { title: label, status: "published", slug: resource },
      );
  }, [items, label, resource]);
  if (!form) return <Loader2 className="h-6 w-6 animate-spin text-dir-gold" />;
  const save = async () => {
    setSaving(true);
    try {
      const r = form.id
        ? await api.put(`/cms/${resource}/${form.id}`, form)
        : await api.post(`/cms/${resource}`, form);
      setForm(r.data);
      toast.success(`${label} published`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };
  return (
    <div className="max-w-3xl space-y-5">
      <div className={card}>
        {fields.map(({ key, label: fieldLabel, type = "text" }) => (
          <label key={key} className="mb-4 block">
            <span className="mb-1.5 block text-sm text-dir-muted">
              {fieldLabel}
            </span>
            {type === "textarea" ? (
              <textarea
                rows={4}
                className={input}
                value={form[key] || ""}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              />
            ) : type === "bool" ? (
              <input
                type="checkbox"
                checked={!!form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
              />
            ) : (
              <input
                className={input}
                value={form[key] || ""}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              />
            )}
          </label>
        ))}
      </div>
      <button
        disabled={saving}
        onClick={save}
        className="inline-flex items-center gap-2 rounded-lg bg-dir-gold px-5 py-2.5 font-bold text-dir-deep"
      >
        <Save className="h-4 w-4" />
        Publish {label}
      </button>
    </div>
  );
}

export function NavigationManager() {
  const [items] = useCmsList("navigation");
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    if (items) setDoc(items[0]);
  }, [items]);
  if (!doc) return <Loader2 className="h-6 w-6 animate-spin text-dir-gold" />;
  const update = (id, patch) =>
    setDoc({
      ...doc,
      items: doc.items.map((x) => (x.id === id ? { ...x, ...patch } : x)),
    });
  const save = async () => {
    setDoc((await api.put(`/cms/navigation/${doc.id}`, doc)).data);
    toast.success("Navigation published");
  };
  return (
    <div className="max-w-4xl">
      <Reorder.Group
        values={doc.items}
        onReorder={(v) => setDoc({ ...doc, items: v })}
        className="space-y-3"
      >
        {doc.items.map((it) => (
          <Reorder.Item
            key={it.id}
            value={it}
            className={`${card} flex flex-wrap items-center gap-3`}
          >
            <GripVertical className="h-5 w-5 cursor-grab text-dir-faint" />
            <input
              className={`${input} flex-1`}
              value={it.title}
              onChange={(e) => update(it.id, { title: e.target.value })}
            />
            <input
              className={`${input} flex-1`}
              value={it.url}
              onChange={(e) => update(it.id, { url: e.target.value })}
            />
            <label className="text-sm text-dir-muted">
              <input
                type="checkbox"
                checked={it.visible}
                onChange={(e) => update(it.id, { visible: e.target.checked })}
              />{" "}
              Visible
            </label>
            <button
              onClick={() =>
                setDoc({
                  ...doc,
                  items: doc.items.filter((x) => x.id !== it.id),
                })
              }
              className="text-red-400"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </Reorder.Item>
        ))}
      </Reorder.Group>
      <div className="mt-4 flex gap-3">
        <button
          onClick={() =>
            setDoc({
              ...doc,
              items: [
                ...doc.items,
                {
                  id: crypto.randomUUID(),
                  title: "New item",
                  url: "/",
                  visible: true,
                  new_tab: false,
                },
              ],
            })
          }
          className="inline-flex items-center gap-2 rounded-lg border border-dir-gold/30 px-4 py-2 text-dir-gold"
        >
          <Plus className="h-4 w-4" />
          Add item
        </button>
        <button
          onClick={save}
          className="rounded-lg bg-dir-gold px-5 py-2 font-bold text-dir-deep"
        >
          Publish Navigation
        </button>
      </div>
    </div>
  );
}

export function HomepageManager() {
  const [items] = useCmsList("homepage");
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    if (items) setDoc(items[0]);
  }, [items]);
  if (!doc) return <Loader2 className="h-6 w-6 animate-spin text-dir-gold" />;
  const save = async () => {
    setDoc((await api.put(`/cms/homepage/${doc.id}`, doc)).data);
    toast.success("Homepage order published");
  };
  return (
    <div className="max-w-3xl">
      <p className="mb-4 text-sm text-dir-muted">
        Drag sections to change their public order. Hidden sections remain
        editable but do not render publicly.
      </p>
      <Reorder.Group
        values={doc.sections}
        onReorder={(v) => setDoc({ ...doc, sections: v })}
        className="space-y-3"
      >
        {doc.sections.map((s) => (
          <Reorder.Item
            key={s.id}
            value={s}
            className={`${card} flex items-center gap-4`}
          >
            <GripVertical className="h-5 w-5 cursor-grab text-dir-faint" />
            <div className="flex-1">
              <div className="font-medium text-dir-text">{s.title}</div>
              <div className="text-xs uppercase text-dir-faint">{s.type}</div>
            </div>
            <label className="text-sm text-dir-muted">
              <input
                type="checkbox"
                checked={s.visible}
                onChange={(e) =>
                  setDoc({
                    ...doc,
                    sections: doc.sections.map((x) =>
                      x.id === s.id ? { ...x, visible: e.target.checked } : x,
                    ),
                  })
                }
              />{" "}
              Visible
            </label>
          </Reorder.Item>
        ))}
      </Reorder.Group>
      <button
        onClick={save}
        className="mt-5 rounded-lg bg-dir-gold px-5 py-2.5 font-bold text-dir-deep"
      >
        Publish Homepage Order
      </button>
    </div>
  );
}

export function ActivityLog() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/cms/activity").then((r) => setItems(r.data));
  }, []);
  if (!items) return <Loader2 className="h-6 w-6 animate-spin text-dir-gold" />;
  return (
    <div className="overflow-hidden rounded-2xl border border-dir-gold/15">
      {items.map((a) => (
        <div
          key={a.id}
          className="grid gap-2 border-b border-dir-gold/10 p-4 text-sm sm:grid-cols-[180px_1fr_220px]"
        >
          <span className="capitalize text-dir-gold">
            {a.action} {a.resource}
          </span>
          <span className="text-dir-text">{a.summary || a.resource_id}</span>
          <span className="text-dir-faint">
            {a.user_email} · {new Date(a.timestamp).toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

export function TaxonomyManager({resource="categories",label="Categories"}) {
  const [items,setItems]=useState(null),[edit,setEdit]=useState(null);
  const load=useCallback(()=>api.get(`/cms/${resource}`).then(r=>setItems(r.data)).catch(()=>setItems([])),[resource]);
  useEffect(()=>{ load(); },[load]);
  const save=async e=>{e.preventDefault();const payload={...edit,sort_order:Number(edit.sort_order||0),content_types:resource==="categories"?(edit.content_types||[]):undefined};try{edit.id?await api.put(`/cms/${resource}/${edit.id}`,payload):await api.post(`/cms/${resource}`,payload);toast.success(`${label} saved`);setEdit(null);load()}catch(err){toast.error(err.response?.data?.detail||"Save failed")}};
  const archive=async id=>{await api.delete(`/cms/${resource}/${id}`);load()};
  const move=async(index,delta)=>{const other=items[index+delta];if(!other)return;await Promise.all([api.put(`/cms/${resource}/${items[index].id}`,{...items[index],sort_order:other.sort_order}),api.put(`/cms/${resource}/${other.id}`,{...other,sort_order:items[index].sort_order})]);load()};
  return <div data-testid={`taxonomy-manager-${resource}`}><div className="mb-6 flex items-center justify-between"><div><h2 className="font-display text-2xl text-dir-text">{label}</h2><p className="mt-1 text-sm text-dir-muted">Global options managed by Admin and Super Admin.</p></div><button onClick={()=>setEdit({name:"",slug:"",description:"",content_types:["blog","article"],status:"active",sort_order:(items?.length||0)*10})} className="rounded-lg bg-dir-gold px-4 py-2 font-bold text-dir-deep">Add {resource==="categories"?"category":"author"}</button></div>{!items?<Loader2 className="animate-spin text-dir-gold"/>:<div className="overflow-x-auto rounded-2xl border border-dir-gold/15"><table className="w-full text-left text-sm"><thead className="bg-dir-deep text-dir-faint"><tr><th className="p-4">Name</th><th>Status</th><th>Order</th><th className="p-4 text-right">Actions</th></tr></thead><tbody>{items.map((x,i)=><tr key={x.id} className="border-t border-dir-gold/10"><td className="p-4 text-dir-text"><div>{x.name}</div><div className="text-xs text-dir-faint">/{x.slug}</div></td><td className="text-dir-muted">{x.status}</td><td className="text-dir-muted">{x.sort_order}</td><td className="p-4"><div className="flex justify-end gap-2"><button onClick={()=>move(i,-1)} className="text-dir-muted" aria-label="Move up">↑</button><button onClick={()=>move(i,1)} className="text-dir-muted" aria-label="Move down">↓</button><button onClick={()=>setEdit({...x})} className="text-dir-gold">Edit</button><button onClick={()=>archive(x.id)} className="text-red-400">Archive</button></div></td></tr>)}</tbody></table></div>}{edit&&<div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4"><form onSubmit={save} className="w-full max-w-xl rounded-2xl border border-dir-gold/20 bg-dir-card p-6"><div className="flex justify-between"><h3 className="font-display text-xl text-dir-text">Edit {resource==="categories"?"category":"author"}</h3><button type="button" onClick={()=>setEdit(null)}><X className="text-dir-muted"/></button></div><input required className={`${input} mt-5`} placeholder="Name" value={edit.name} onChange={e=>setEdit({...edit,name:e.target.value})}/><input className={`${input} mt-3`} placeholder="Slug" value={edit.slug||""} onChange={e=>setEdit({...edit,slug:e.target.value})}/><textarea className={`${input} mt-3`} rows="3" placeholder="Description" value={edit.description||""} onChange={e=>setEdit({...edit,description:e.target.value})}/>{resource==="categories"&&<div className="mt-4"><div className="text-xs uppercase text-dir-faint">Content types</div><div className="mt-2 flex flex-wrap gap-2">{["blog","article","news","event","opportunity","media","heritage"].map(t=><label key={t} className="flex items-center gap-1 text-sm text-dir-muted"><input type="checkbox" checked={(edit.content_types||[]).includes(t)} onChange={e=>setEdit({...edit,content_types:e.target.checked?[...(edit.content_types||[]),t]:(edit.content_types||[]).filter(x=>x!==t)})}/>{t}</label>)}</div></div>}<div className="mt-4 grid grid-cols-2 gap-3"><select className={input} value={edit.status||"active"} onChange={e=>setEdit({...edit,status:e.target.value})}><option value="active">Active</option><option value="archived">Archived</option></select><input type="number" className={input} value={edit.sort_order||0} onChange={e=>setEdit({...edit,sort_order:e.target.value})}/></div><button className="mt-5 w-full rounded-lg bg-dir-gold px-4 py-2 font-bold text-dir-deep">Save</button></form></div>}</div>
}
